import { SceneCode } from '@/apis/recommend/recommend.interface';
import type { Response } from '@/app/csr-src/apis/api.interface';
import { fetchUserCenterAccount } from '@/app/csr-src/apis/profile/profile.api';
import type {
  UserAccountType,
  UserCenterAccountResponse,
} from '@/app/csr-src/apis/profile/profile.interface';
import BreadCrumb from '@/components/breadCrumb';
import SideMenu, { SideMenuHandle } from '@/components/sideMenu';
import {
  EXPOSURE_RECORD as orderList_EXPOSURE_RECORD,
  sendClickTrack as orderList_sendClickTrack,
} from '@/tracks/25352';
import {
  EXPOSURE_RECORD as wishlist_EXPOSURE_RECORD,
  sendClickTrack as wishlist_sendClickTrack,
} from '@/tracks/25353';
import {
  EXPOSURE_RECORD as myReviews_EXPOSURE_RECORD,
  sendClickTrack as myReviews_sendClickTrack,
} from '@/tracks/25356';
import {
  EXPOSURE_RECORD as history_EXPOSURE_RECORD,
  sendClickTrack as history_sendClickTrack,
} from '@/tracks/26360';
import { observer } from 'mobx-react';
import { lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import styles from './profileList.module.scss';

type PathToPageNameKey = {
  [key: string]: string;
};

type RecommendSceneKey = {
  [key: string]: string;
};
const RecommendClient = lazy(() => import('@/components/recommend/recommendClient'));

const ProfileList = observer(() => {
  const { t } = useTranslation();
  const SideMenuRef = useRef<SideMenuHandle>(null);
  const location = useLocation();
  const currentPath = location.pathname || '';

    const pathToPageNameKey: PathToPageNameKey = {
      "/profile/my-orders": "myOrders",
      "/profile/my-returns": "returns",
      "/profile/messages": "messages",
      "/profile/coupons-discounts": "coupons",
      "/profile/my-reviews": "myReviews",
      "/profile/wishlist": "wishlist",
      "/profile/my-address": "addresses",
      // "/profile/shopping-activity": "browsingHistory",
      "/profile/points-activity":"points"
    };

    const recommendSceneKey: RecommendSceneKey = {
      "/profile/my-orders": "order_list",
      "/profile/my-returns": "after_sales",
      "/profile/messages": "message_box",
      "/profile/my-reviews": "review_list",
      "/profile/wishlist": "wish_list",
      // "/profile/shopping-activity": "browse_history",
      "/profile/points-activity":"points"
    };

  const currentPageNameKey: string = pathToPageNameKey[currentPath.toLowerCase()];
  const recommendSceneCode: SceneCode = recommendSceneKey[currentPath.toLowerCase()] as SceneCode;
  const [orderNum, setOrderNum] = useState(0);
  const [couponsNum, setCouponsNum] = useState(0);
  const [recommendMainSkus, setRecommendMainSkus] = useState(''); // 推荐商品列表

  const initCartNumHandler = () => {
    window?.JDIheader?.initCartNum();
  };
  const updateNumber = () => {
    getAllNumber();
  };
  const initRecommendMainSkus = (mainSkus: string) => {
    setRecommendMainSkus(mainSkus);
  };
  // 获取全部的角标数量
  const getAllNumber = () => {
    Promise.all([fetchUserCenterAccount([3, 6, 9])]).then(
      (data: [Response<UserCenterAccountResponse>]) => {
        const res = data[0]; // 获取订单角标数量
        if (res.code === '200') {
          const { accountList } = res.data;
          accountList.length &&
            accountList.map((item: UserAccountType) => {
              switch (item.type) {
                case 9: // 订单数量
                  setOrderNum(Number(item.count > 0 ? item.count : 0));
                  break;
                case 3: // 优惠券数量
                  setCouponsNum(Number(item.count > 0 ? item.count : 0));
                  break;
              }
            });
        }
      },
    );
  };

  // 获取埋点参数
  const getTrackParams = (recommendSceneCode: string) => {
    switch (recommendSceneCode) {
      case 'order_list':
        return {
          RecomSku_Expo: orderList_EXPOSURE_RECORD.OrderCenter_List_RecomSku_Expo,
          RecomSku_Tab_Expo: orderList_EXPOSURE_RECORD.OrderCenter_List_RecomSku_Tab_Expo,
          RecomSku_Addcart_Expo: orderList_EXPOSURE_RECORD.OrderCenter_List_RecomSku_Addcart_Expo,
          productClick: orderList_sendClickTrack.OrderCenter_List_RecomSku,
          tabClick: orderList_sendClickTrack.OrderCenter_List_RecomSku_Tab,
          addCartClick: orderList_sendClickTrack.OrderCenter_List_RecomSku_Addcart,
        };
      case 'wish_list':
        return {
          RecomSku_Expo: wishlist_EXPOSURE_RECORD.MyJD_Wishlist_RecomSku_Expo,
          RecomSku_Tab_Expo: wishlist_EXPOSURE_RECORD.MyJD_Wishlist_RecomSku_Tab_Expo,
          RecomSku_Addcart_Expo: wishlist_EXPOSURE_RECORD.MyJD_Wishlist_RecomSku_Addcart_Expo,
          productClick: wishlist_sendClickTrack.MyJD_Wishlist_RecomSku,
          tabClick: wishlist_sendClickTrack.MyJD_Wishlist_RecomSku_Tab,
          addCartClick: wishlist_sendClickTrack.MyJD_Wishlist_RecomSku_Addcart,
        };
      case 'review_list':
        return {
          RecomSku_Expo: myReviews_EXPOSURE_RECORD.Evaluate_CommentComplete_RecomSku_Expo,
          RecomSku_Tab_Expo: myReviews_EXPOSURE_RECORD.Evaluate_CommentComplete_RecomSku_Tab_Expo,
          RecomSku_Addcart_Expo:
            myReviews_EXPOSURE_RECORD.Evaluate_CommentComplete_RecomSku_Addcart_Expo,
          productClick: myReviews_sendClickTrack.Evaluate_CommentComplete_RecomSku,
          tabClick: myReviews_sendClickTrack.Evaluate_CommentComplete_RecomSku_Tab,
          addCartClick: myReviews_sendClickTrack.Evaluate_CommentComplete_RecomSku_Addcart,
        };
      default:
        return;
    }
  };

  useEffect(() => {
    getAllNumber();
  }, []);

  return (
    <div className={styles.profileList_container}>
      <div className={styles.w}>
        <BreadCrumb
          list={[
            { name: t(`common.pageName.me`), url: '/profile' },
            { name: t("points.title1") },
          ]}
        />
        <div className={styles.main_container}>
          <SideMenu ref={SideMenuRef} orderNum={orderNum} couponsNum={couponsNum} />
          <Outlet context={{ updateNumber, initCartNumHandler, initRecommendMainSkus }} />
        </div>
        <div className={styles.Recommend}>
          {recommendSceneCode &&
            ((['order_list', 'review_list'].includes(recommendSceneCode) && recommendMainSkus) ||
              !['order_list', 'review_list'].includes(recommendSceneCode)) && (
              <RecommendClient
                sceneCode={recommendSceneCode}
                mainSkus={recommendMainSkus}
                trackParams={getTrackParams(recommendSceneCode)}
              />
            )}
        </div>
      </div>
    </div>
  );
});
export default ProfileList;
